<template>
    <FormSection title="Referral Information" :full-height="false" :application-id="application">
        <template #content>
            <div class="row py-3 mx-1 px-4">
                <div class="col-12 row g-0 d-flex">
                    <div class="col-12 col-lg-6 pe-lg-3">
                        <div class="form-group">
                            <label for="referral-reason">
                                Referral Reason
                                <span class="text-danger">*</span>
                            </label>
                            <textarea
                                class="form-control"
                                name="referral-reason"
                                v-model="patientDetails.referralInformation.referralReason"
                                placeholder="Enter Reason"
                                maxlength="500"
                                @blur="v$.patientDetails.referralInformation.referralReason.$touch"
                                :class="`${
                                    v$.patientDetails.referralInformation.referralReason.$error ? 'field-invalid' : ''
                                }`"
                                @change="(e) => handleAllInput(e.target.value, 'referralReason')"
                            ></textarea>
                        </div>
                        <span v-if="v$.patientDetails.referralInformation.referralReason.$error" class="field-invalid"
                            >Referral reason is required.</span
                        >
                    </div>
                    <div class="form-group col-12 col-lg-4 pe-lg-2">
                        <div class="form-group">
                            <label for="referral-date">
                                Referral Date
                                <span class="text-danger">*</span>
                            </label>
                            <DatePicker :options="datePickerOptions" :getDate="handleDateChange">
                                <div class="input-group date datepickerInput">
                                    <input
                                        name="referralDate"
                                        type="text"
                                        class="form-control"
                                        placeholder="MM/DD/YYYY"
                                        :value="formatDateInput(patientDetails.referralInformation.referralDate)"
                                        @change="(e) => handleDateChange(e.target.value, e.target.name)"
                                        autocomplete="off"
                                        @blur="v$.patientDetails.referralInformation.referralDate.$touch"
                                        :class="`${
                                            v$.patientDetails.referralInformation.referralDate.$error
                                                ? 'field-invalid'
                                                : ''
                                        }`"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                    <span
                                        v-if="v$.patientDetails.referralInformation.referralDate.$error"
                                        class="field-invalid"
                                        >{{
                                            v$.patientDetails.referralInformation.referralDate.$errors[0].$message
                                        }}</span
                                    >
                                </div>
                            </DatePicker>
                        </div>
                        <div class="form-group">
                            <label for="referral-service-location"> Patient Current Location </label>
                            <input
                                class="form-control"
                                name="referral-service-location"
                                maxlength="50"
                                type="text"
                                placeholder="Enter Current Location"
                                v-model="patientDetails.referralInformation.patientCurrentLocation"
                            />
                        </div>
                        <div class="form-group">
                            <label for="inquiry-date"> Inquiry Date </label>
                            <DatePicker>
                                <div class="input-group date datepickerInput">
                                    <input
                                        id="inquiry-date"
                                        name="inquiryDate"
                                        type="text"
                                        class="form-control"
                                        placeholder="MM/DD/YYYY"
                                        :value="formatDateInput(patientDetails.referralInformation.inquiryDate)"
                                        autocomplete="off"
                                        disabled
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                </div>
                                <span v-if="!patientDetails.referralInformation.inquiryDate" class="text-secondary"
                                    >Not Applicable</span
                                >
                            </DatePicker>
                        </div>
                    </div>
                    <div class="form-group col-12 d-flex flex-column">
                        <label for="source-of-referral">
                            Referral Source
                            <span class="text-danger">*</span>
                        </label>
                        <div class="d-flex flex-wrap" v-if="referralSources.length > 0">
                            <div
                                v-for="source of referralSources"
                                :key="source.id"
                                class="form-check custom-radio form-check-inline d-inline"
                            >
                                <input
                                    type="radio"
                                    :id="`source-of-referral-${source.name}`"
                                    class="form-check-input"
                                    name="source-of-referral"
                                    :checked="patientDetails.referralInformation.sourceOfReferral === source.value"
                                    :value="source.value"
                                    v-model="patientDetails.referralInformation.sourceOfReferral"
                                    @change="sourceOfReferralUpdated()"
                                />
                                <label class="form-check-label" :for="`source-of-referral-${source.name}`">
                                    <span class="me-1">{{ source.name }}</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <section class="col-12 card flex-row py-2 px-3 row g-0 mb-2">
                        <template v-if="patientDetails.referralInformation.sourceOfReferral == 1">
                            <div class="form-group col-12 col-lg-4 pe-lg-3">
                                <label for="referral-source">
                                    <span>Facility Name <span class="text-danger">*</span></span>
                                </label>
                                <TypeaheadInput
                                    :getData="getVendorList"
                                    :value="facility"
                                    v-on:valueChanged="updateFacility"
                                    placeholder="Start Typing..."
                                />
                            </div>
                            <div class="form-group col-12 col-lg-4 pe-lg-3">
                                <label for="referral-name">
                                    <span>Facility Point of Contact Name</span>
                                </label>
                                <input
                                    class="form-control"
                                    maxlength="50"
                                    type="text"
                                    v-model="patientDetails.referralInformation.facilityReferralSourceContactName"
                                    placeholder="Enter Name"
                                />
                            </div>
                            <div class="form-group col-12 col-lg-4 pe-lg-3">
                                <label for="referring-provider"> Referring Provider </label>
                                <TypeaheadInput
                                    :getData="getPhysicianList"
                                    :value="referringProvider"
                                    v-on:valueChanged="updateReferringProvider"
                                    :init-value="referringProvider"
                                    placeholder="Search Referring Provider"
                                />
                            </div>
                            <div class="form-group col-12 col-lg-3 pe-lg-2">
                                <label for="community-liaison"> Community Liaison </label>
                                <TypeaheadInput
                                    :getData="getUsersByRole"
                                    :value="communityLiaison"
                                    v-on:valueChanged="updateCommunityLiaison"
                                    :init-value="communityLiaison"
                                    placeholder="Search Community Liaison"
                                />
                            </div>
                        </template>
                        <template v-if="patientDetails.referralInformation.sourceOfReferral == 2">
                            <div class="form-group col-12 col-lg-4 pe-lg-3">
                                <label for="referral-source">
                                    Referring Provider Name <span class="text-danger">*</span>
                                </label>
                                <TypeaheadInput
                                    :getData="getPhysicianList"
                                    :value="physician"
                                    v-on:valueChanged="updatePhysician"
                                    :init-value="physician"
                                    placeholder="Start Typing..."
                                />
                            </div>
                            <div class="form-group col-12 col-lg-4 pe-lg-3">
                                <label for="referral-name">
                                    <span>Provider Point of Contact Name</span>
                                </label>
                                <input
                                    class="form-control"
                                    maxlength="50"
                                    type="text"
                                    placeholder="Enter Name"
                                    v-model="patientDetails.referralInformation.physicianReferralSourceContactName"
                                />
                            </div>
                            <div class="form-group col-12 col-lg-4 pe-lg-3">
                                <label for="referring-facility">
                                    <span>Referring Facility</span>
                                </label>
                                <TypeaheadInput
                                    :getData="getVendorList"
                                    :value="referringFacility"
                                    v-on:valueChanged="updateReferringFacility"
                                    placeholder="Search Referring Facility"
                                />
                            </div>
                            <div class="form-group col-12 col-lg-3 pe-lg-2">
                                <label for="community-liaison"> Community Liaison </label>
                                <TypeaheadInput
                                    :getData="getUsersByRole"
                                    :value="communityLiaison"
                                    v-on:valueChanged="updateCommunityLiaison"
                                    :init-value="communityLiaison"
                                    placeholder="Search Community Liaison"
                                />
                            </div>
                            <div class="w-100"></div>
                        </template>
                        <template v-if="patientDetails.referralInformation.sourceOfReferral == 3">
                            <div class="form-group col-12 col-lg-4 pe-lg-2">
                                <label for="internal-referral-source">
                                    Other Referral Source Name <span class="text-danger">*</span>
                                </label>
                                <input
                                    class="form-control"
                                    maxlength="50"
                                    type="text"
                                    placeholder="Enter Name of Referral Source"
                                    v-model="patientDetails.referralInformation.otherReferralSourceName"
                                />
                            </div>
                            <div class="form-group col-12 col-lg-4 pe-lg-2">
                                <label for="referral-source">
                                    <span>Relationship</span>
                                </label>
                                <select
                                    name="relationship"
                                    class="form-select"
                                    v-model="patientDetails.referralInformation.otherReferralSourceRelationship"
                                >
                                    <option :value="null">Select Relationship</option>
                                    <option v-for="rel of relationships" :key="rel.name" :value="rel.value">
                                        {{ rel.name }}
                                    </option>
                                </select>
                            </div>

                            <div class="form-group col-12 col-lg-3 pe-lg-2">
                                <label for="community-liaison"> Community Liaison </label>
                                <TypeaheadInput
                                    :getData="getUsersByRole"
                                    :value="communityLiaison"
                                    v-on:valueChanged="updateCommunityLiaison"
                                    :init-value="communityLiaison"
                                    placeholder="Search Community Liaison"
                                />
                            </div>
                        </template>
                        <template v-if="patientDetails.referralInformation.sourceOfReferral == 4">
                            <div class="form-group col-12 col-lg-4 pe-lg-2">
                                <label for="internal-referral-source-name">
                                    Internal Referral Source Name <span class="text-danger">*</span>
                                </label>
                                <TypeaheadInput
                                    :getData="getUsersByRole"
                                    :value="internalReferralSourceName"
                                    v-on:valueChanged="updateInternalReferralSourceName"
                                    :init-value="internalReferralSourceName"
                                    placeholder="Start Typing..."
                                />
                            </div>
                            <div class="form-group col-12 col-lg-4 pe-lg-2">
                                <label for="internal-referral-source"> Internal Referral Source </label>
                                <select
                                    name="internalReferralSource"
                                    class="form-select"
                                    v-model="patientDetails.referralInformation.internalReferralSource"
                                    @change="
                                        onReferralSourceChange(
                                            patientDetails.referralInformation.internalReferralSource
                                        )
                                    "
                                >
                                    <option :value="0">Select Source</option>
                                    <option
                                        v-for="data of InternalReferralSourceEnum"
                                        :key="data.name"
                                        :value="data.value"
                                    >
                                        {{ data.name }}
                                    </option>
                                </select>
                            </div>
                            <!-- <div class="form-group col-12 col-lg-4 pe-lg-3">
                                <label for="referring-facility">
                                    <span>Referring Facility</span>
                                </label>
                                <TypeaheadInput
                                    :getData="getVendorList"
                                    :value="referringFacility"
                                    v-on:valueChanged="updateReferringFacility"
                                    placeholder="Search Referring Facility"
                                />
                            </div> -->
                            <div class="form-group col-12 col-lg-3 pe-lg-2">
                                <label for="community-liaison"> Community Liaison </label>
                                <TypeaheadInput
                                    :getData="getUsersByRole"
                                    :value="communityLiaison"
                                    v-on:valueChanged="updateCommunityLiaison"
                                    :init-value="communityLiaison"
                                    placeholder="Search Community Liaison"
                                />
                            </div>
                        </template>
                    </section>

                    <div class="w-100"></div>
                    <div class="form-group col-12 col-lg-4 pe-lg-2">
                        <label for="referral-comments"> Comments </label>
                        <CommentInput
                            input-name="referral-comments"
                            :result="patientDetails.referralInformation.comments"
                            v-on:resultUpdatedAppended="resultUpdatedAppended"
                            v-on:resultUpdated="resultUpdated"
                            placeholder="Enter Comments"
                            :class-list="commentInputClass"
                            :application="application"
                        >
                        </CommentInput>
                    </div>
                    <div class="form-group col-12 col-lg-4">
                        <label for="referral-reason"> Pending Reason </label>
                        <PendingReason
                            input-name="referral-reason"
                            :result="patientDetails.referralInformation.pendingReasonComment"
                            :classList="commentInputClass"
                            :maxLength="250"
                            placeholder="Enter Comments"
                        ></PendingReason>
                    </div>
                </div>
            </div>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./ReferralInformationSection.ts"></script>
